import { useState, useEffect, useCallback, useRef } from 'react';
import { audioService } from '../apis/audioService';
import {
  createSignedUrlRefresher,
  SIGNED_URL_REFRESH_SKEW_MS,
} from '../utils/signedUrlRefresh';

let globalAudioCache = new Map();
let globalLastFetchTime = null;
let globalIsLoading = false;
let globalFetchPromise = null;
let globalCacheUserId = null;
const cacheListeners = new Set();

// In-memory blob URLs so seeking skips without rebuffering remote S3 ranges.
// Key: sessionId → { blobUrl, createdAt, size }
const globalBlobUrlCache = new Map();
const blobInflight = new Map();
const MAX_BLOB_CACHE_ENTRIES = 4;
const MAX_BLOB_BYTES=60 * 1024 * 1024; //1;  skip blobbing huge visits; stream instead


// Pre-signed playback URLs expire (backend returns `expiresIn`). We remember when each one dies
// so we can reuse it while it is fresh and replace it shortly BEFORE it expires.
// Key: sessionId → { url, expiresAt, ttlMs, ownerUserId }
const globalSignedUrlCache = new Map();
const signedUrlInflight = new Map();
const DEFAULT_SIGNED_URL_TTL_S = 900;
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;
const STORAGE_PREFIX = 'vbot_audio_inventory_v1:';

const broadcastCacheState = () => {
  cacheListeners.forEach((listener) =>
    listener({
      audioCache: globalAudioCache,
      isLoading: globalIsLoading,
      lastFetchTime: globalLastFetchTime,
    }),
  );
};

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId}`;
}

function countConfirmedEntries(map) {
  let n = 0;
  map.forEach((v) => {
    if (v && !v.isOptimistic) n += 1;
  });
  return n;
}

function persistInventory(userId) {
  if (!userId || typeof sessionStorage === 'undefined') return;
  try {
    const files = [];
    globalAudioCache.forEach((file, sessionId) => {
      if (!file || file.isOptimistic) return;
      files.push({
        sessionId: String(sessionId),
        filename: file.filename,
        s3Key: file.s3Key,
        size: file.size,
        contentType: file.contentType || file.mimeType,
        lastModified: file.lastModified,
        metadata: file.metadata,
        isExpired: !!file.isExpired,
        isAccessible: file.isAccessible !== false,
      });
    });
    sessionStorage.setItem(
      storageKey(userId),
      JSON.stringify({ at: globalLastFetchTime || Date.now(), files }),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function restoreInventory(userId) {
  if (!userId || typeof sessionStorage === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.files) || parsed.files.length === 0) {
      return false;
    }
    const restored = new Map();
    parsed.files.forEach((file) => {
      if (!file?.sessionId) return;
      const sid = String(file.sessionId);
      restored.set(sid, {
        ...file,
        sessionId: sid,
        isOptimistic: false,
        isAccessible: file.isAccessible !== false,
        isExpired: !!file.isExpired,
      });
    });
    if (restored.size === 0) return false;
    // Keep any in-memory optimistic entries for this user.
    globalAudioCache.forEach((existing, sid) => {
      if (existing?.isOptimistic && !restored.has(String(sid))) {
        restored.set(String(sid), existing);
      }
    });
    globalAudioCache = restored;
    globalLastFetchTime = parsed.at || Date.now();
    globalCacheUserId = String(userId);
    broadcastCacheState();
    return true;
  } catch {
    return false;
  }
}

function clearPersistedInventory(userId) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (userId) {
      sessionStorage.removeItem(storageKey(userId));
      return;
    }
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

function isAudioExpired(lastModified, retentionDays = 7) {
  if (!lastModified) return false;
  const retentionMs = (retentionDays || 7) * 24 * 60 * 60 * 1000;
  const expirationTime = new Date(lastModified).getTime() + retentionMs;
  return Date.now() > expirationTime;
}

function isCacheValidNow() {
  if (!globalLastFetchTime) return false;
  return Date.now() - globalLastFetchTime < CACHE_DURATION_MS;
}

function touchBlobCache(sessionId) {
  const entry = globalBlobUrlCache.get(sessionId);
  if (!entry) return;
  globalBlobUrlCache.delete(sessionId);
  globalBlobUrlCache.set(sessionId, { ...entry, createdAt: Date.now() });
}

function evictOldestBlobIfNeeded() {
  while (globalBlobUrlCache.size > MAX_BLOB_CACHE_ENTRIES) {
    const oldestKey = globalBlobUrlCache.keys().next().value;
    const oldest = globalBlobUrlCache.get(oldestKey);
    if (oldest?.blobUrl) {
      try {
        URL.revokeObjectURL(oldest.blobUrl);
      } catch {
        /* ignore */
      }
    }
    globalBlobUrlCache.delete(oldestKey);
  }
}

async function fetchAsBlobUrl(signedUrl) {
  const res = await fetch(signedUrl, { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`Audio fetch failed (${res.status})`);
  const len = Number(res.headers.get('content-length') || 0);
  if (len > MAX_BLOB_BYTES) {
    throw new Error('Audio too large to blob-cache');
  }
  const blob = await res.blob();
  if (blob.size > MAX_BLOB_BYTES) {
    throw new Error('Audio too large to blob-cache');
  }
  return { blobUrl: URL.createObjectURL(blob), size: blob.size };
}

function getFreshSignedEntry(sid, ownerUserId) {
  const entry = globalSignedUrlCache.get(sid);
  if (!entry) return null;
  if ((entry.ownerUserId || null) !== (ownerUserId || null)) return null;
  if (entry.expiresAt - SIGNED_URL_REFRESH_SKEW_MS <= Date.now()) return null;
  return entry;
}

/**
 * Get a signed playback URL for a session, remembering when it expires.
 * Reuses the cached URL while it still has more than the refresh skew left;
 * otherwise (or with `force`) asks the backend for a new one. Concurrent callers share one request.
 * @returns {Promise<{ url: string, expiresAt: number, ttlMs: number, ownerUserId: string|null } | null>}
 */
export async function fetchSignedAudioUrl(sessionId, options = {}, { force = false } = {}) {
  const sid = String(sessionId);
  const ownerUserId = options?.ownerUserId ?? null;

  if (!force) {
    const hit = getFreshSignedEntry(sid, ownerUserId);
    if (hit) return hit;
  }

  const key = `${sid}:${ownerUserId || ''}`;
  if (signedUrlInflight.has(key)) return signedUrlInflight.get(key);

  const request = (async () => {
    // Measure from BEFORE the request so network latency can never make us over-estimate the lifetime.
    const requestedAt = Date.now();
    const response = await audioService.getAudioFileUrl(sid, options);
    if (response?.pending) return null;
    if (!response?.success || !response.data?.url) return null;

    const ttlSeconds =
      Number(response.data.expiresIn) > 0
        ? Number(response.data.expiresIn)
        : Number(options?.expiresIn) > 0
          ? Number(options.expiresIn)
          : DEFAULT_SIGNED_URL_TTL_S;
    const entry = {
      url: response.data.url,
      expiresAt: requestedAt + ttlSeconds * 1000,
      ttlMs: ttlSeconds * 1000,
      ownerUserId,
    };
    globalSignedUrlCache.set(sid, entry);
    return entry;
  })();

  signedUrlInflight.set(key, request);
  try {
    return await request;
  } finally {
    if (signedUrlInflight.get(key) === request) signedUrlInflight.delete(key);
  }
}

/**
 * Swap an <audio> element to a new signed URL (`entry` from fetchSignedAudioUrl), keeping
 * position / rate / volume and resuming playback if it was playing.
 * Resolves once the new source is ready (or failed / timed out).
 */
function swapAudioSrc(audio, entry) {
  const wasPlaying = !audio.paused && !audio.ended;
  const time = audio.currentTime || 0;
  const rate = audio.playbackRate || 1;
  const volume = audio.volume;

  // Lets error handlers (e.g. session-manager) ignore the transient reload.
  audio._vbotHotSwap = true;
  audio._vbotRecoveryFailed = false;
  audio._vbotUrlExpiresAt = entry.expiresAt;

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('error', finish);
      audio._vbotHotSwap = false;
      resolve();
    };

    function onMeta() {
      try {
        if (Number.isFinite(time) && time > 0) audio.currentTime = time;
        audio.playbackRate = rate;
        audio.volume = volume;
      } catch {
        /* ignore */
      }
      if (wasPlaying) audio.play().catch(() => {});
      finish();
    }

    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('error', finish);
    timeoutId = setTimeout(finish, 15000);
    audio.src = entry.url;
    audio.preload = 'auto';
  });
}

/**
 * Keep an <audio> element's signed URL from ever interrupting the listener.
 *
 * Rule: never reload an element that is playing, because that always causes an audible gap.
 *  - While PLAYING: shortly before the URL expires we only prefetch a replacement URL into the cache
 *    (no change to the element, so playback continues untouched).
 *  - When the user PAUSES / playback ends: the element is idle, so the fresh URL is swapped in
 *    invisibly (position preserved) and is ready for resume / seeking.
 *  - Safety nets: if the user SEEKS (or the browser errors) after the old URL has already died, we
 *    swap in the fresh URL right then and resume from the same position; the error is hidden from the UI.
 *
 * Attach this BEFORE other listeners on the element so its error/seek handling runs first.
 * Stops by itself for blob: sources or once `audio._vbotCleared` is set.
 * @returns {() => void} cancel function
 */
export function keepAudioElementUrlFresh(audio, sessionId, options = {}) {
  const sid = String(sessionId);
  audio._vbotUrlOptions = options;

  const initial = globalSignedUrlCache.get(sid);
  if (initial && initial.url === audio.src) audio._vbotUrlExpiresAt = initial.expiresAt;

  const isSignedSrc = () => {
    const src = audio.src || '';
    return !!src && src !== window.location.href && !src.startsWith('blob:');
  };
  const urlIsExpired = () =>
    audio._vbotUrlExpiresAt != null && Date.now() >= audio._vbotUrlExpiresAt - 5000;

  const refresher = createSignedUrlRefresher({
    getExpiresAt: () => globalSignedUrlCache.get(sid)?.expiresAt ?? null,
    getTtlMs: () => globalSignedUrlCache.get(sid)?.ttlMs ?? null,
    refresh: async () => {
      if (audio._vbotCleared || !isSignedSrc()) return false;
      if (audio.paused) return false; // idle; the pause handler already swapped the URL in
      // Playing: fetch only. The element keeps streaming; the new URL is picked up at the next pause.
      const fresh = await fetchSignedAudioUrl(sid, options, { force: true });
      if (!fresh?.url) throw new Error('Could not refresh audio URL');
      return true;
    },
  });

  let recovering = false;

  // Swap to a valid URL right now (used when the old one is already dead).
  const recover = async () => {
    if (recovering || audio._vbotCleared) return;
    recovering = true;
    audio._vbotHotSwap = true; // synchronously, so other 'error' handlers ignore this
    try {
      const fresh = await fetchSignedAudioUrl(sid, options);
      if (!fresh?.url || audio._vbotCleared) throw new Error('No fresh audio URL');
      await swapAudioSrc(audio, fresh);
      if (audio.error) throw new Error('Fresh audio URL failed to load');
    } catch {
      audio._vbotHotSwap = false;
      if (!audio._vbotCleared) {
        // Recovery failed: let the normal error handling show it.
        audio._vbotRecoveryFailed = true;
        audio.dispatchEvent(new Event('error'));
      }
    } finally {
      recovering = false;
    }
  };

  const onPause = () => {
    if (audio._vbotHotSwap || audio._vbotCleared || !audio.paused) return;
    // Idle now, so swapping is invisible. No-op unless a newer URL exists or this one is stale.
    ensureAudioElementUrlFresh(audio, sid).catch(() => {});
  };
  const onPlay = () => {
    if (audio._vbotHotSwap || audio._vbotCleared) return;
    refresher.restart({ immediate: true });
  };
  const onSeeking = () => {
    if (audio._vbotHotSwap || audio._vbotCleared || !isSignedSrc()) return;
    if (urlIsExpired()) recover();
  };
  const onError = () => {
    if (audio._vbotHotSwap || audio._vbotCleared || audio._vbotRecoveryFailed) return;
    if (isSignedSrc() && urlIsExpired()) recover();
  };

  audio.addEventListener('error', onError);
  audio.addEventListener('seeking', onSeeking);
  audio.addEventListener('pause', onPause);
  audio.addEventListener('play', onPlay);
  refresher.start();

  return () => {
    audio.removeEventListener('error', onError);
    audio.removeEventListener('seeking', onSeeking);
    audio.removeEventListener('pause', onPause);
    audio.removeEventListener('play', onPlay);
    refresher.cancel();
  };
}

/**
 * Swap a newer / valid signed URL into an IDLE <audio> element (paused or about to be resumed).
 * No-op if the element already has a fresh URL, uses a blob:, or is currently playing.
 */
export async function ensureAudioElementUrlFresh(audio, sessionId) {
  if (!audio || audio._vbotCleared || audio._vbotHotSwap) return;
  if (!audio.paused) return; // never reload a playing element
  const src = audio.src || '';
  if (!src || src.startsWith('blob:') || src === window.location.href) return;

  const sid = String(sessionId);
  const entry = globalSignedUrlCache.get(sid);
  if (!entry) return;
  if (entry.url === src && entry.expiresAt - SIGNED_URL_REFRESH_SKEW_MS > Date.now()) return;

  const fresh = await fetchSignedAudioUrl(sid, audio._vbotUrlOptions || {});
  if (!fresh?.url || fresh.url === src || audio._vbotCleared || !audio.paused) return;
  await swapAudioSrc(audio, fresh);
}

/**
 * Clear in-memory (+ optional persisted) audio inventory. Call on logout.
 */
export function clearAudioInventory(userId = null) {
  globalAudioCache = new Map();
  globalLastFetchTime = null;
  globalIsLoading = false;
  globalFetchPromise = null;
  globalCacheUserId = null;
  globalBlobUrlCache.forEach((entry) => {
    if (entry?.blobUrl) {
      try {
        URL.revokeObjectURL(entry.blobUrl);
      } catch {
        /* ignore */
      }
    }
  });
  globalBlobUrlCache.clear();
  blobInflight.clear();
  globalSignedUrlCache.clear();
  signedUrlInflight.clear();
  clearPersistedInventory(userId);
  broadcastCacheState();
}

/**
 * Prefetch / refresh S3 audio inventory. Safe to call from AuthContext right after login.
 * Single-flight: concurrent callers share one network request (avoids clobber races).
 */
export async function prefetchUserAudioFiles(options = {}) {
  const { force = false, userId = null } = options;
  const uid = userId != null ? String(userId) : globalCacheUserId;

  if (uid && globalCacheUserId && uid !== globalCacheUserId) {
    clearAudioInventory(globalCacheUserId);
  }
  if (uid) {
    globalCacheUserId = uid;
    if (globalAudioCache.size === 0) {
      restoreInventory(uid);
    }
  }

  // Single-flight: never start a parallel force refresh that can wipe a good map.
  if (globalFetchPromise) {
    return globalFetchPromise;
  }

  if (!force && isCacheValidNow() && globalAudioCache.size > 0) {
    return globalAudioCache;
  }

  globalFetchPromise = (async () => {
    try {
      globalIsLoading = true;
      broadcastCacheState();
      const response = await audioService.getUserAudioFiles();

      let processedResponse = response;
      if (response && response.data && response.data.statusCode) {
        processedResponse = {
          success: response.data.statusCode === 200,
          data: response.data.body?.data || response.data.body,
        };
      }

      const files = processedResponse?.success
        ? processedResponse.data?.files
        : null;

      if (!processedResponse?.success || !Array.isArray(files)) {
        // Keep whatever we already have (memory / sessionStorage).
        broadcastCacheState();
        return globalAudioCache;
      }

      // Protect against intermittent empty S3 list responses wiping the sidebar.
      if (files.length === 0 && countConfirmedEntries(globalAudioCache) > 0) {
        console.warn(
          'Audio inventory: ignoring empty S3 list while local cache has entries',
        );
        broadcastCacheState();
        return globalAudioCache;
      }

      const newCache = new Map();
      files.forEach((file) => {
        if (!file?.sessionId) return;
        const sid = String(file.sessionId);
        const uploadStatus = file.uploadStatus || (file.s3Key ? 'ready' : 'ready');
        const expired = isAudioExpired(file.lastModified, file.metadata?.retentionDays);
        const isPending = uploadStatus === 'pending_s3';
        newCache.set(sid, {
          ...file,
          sessionId: sid,
          uploadStatus,
          isExpired: expired,
          // Pending entries remain visible; playback uses local blob or retries URL.
          isAccessible: !expired && uploadStatus !== 'failed',
          isOptimistic: false,
          isPending,
        });
      });

      // Keep optimistic (just-uploaded / live) until S3 lists them.
      globalAudioCache.forEach((existing, sid) => {
        if (existing?.isOptimistic && !newCache.has(String(sid))) {
          newCache.set(String(sid), existing);
        }
      });

      // If server returned a partial/suspicious shrink, retain missing confirmed entries.
      const prevConfirmed = countConfirmedEntries(globalAudioCache);
      if (
        prevConfirmed > 0 &&
        newCache.size > 0 &&
        countConfirmedEntries(newCache) < Math.floor(prevConfirmed * 0.5)
      ) {
        globalAudioCache.forEach((existing, sid) => {
          const key = String(sid);
          if (!existing?.isOptimistic && !newCache.has(key)) {
            newCache.set(key, existing);
          }
        });
        console.warn(
          'Audio inventory: merged retained entries after suspicious shrink',
          { prevConfirmed, next: countConfirmedEntries(newCache) },
        );
      }

      globalAudioCache = newCache;
      globalLastFetchTime = Date.now();
      if (uid) persistInventory(uid);
      broadcastCacheState();
      return newCache;
    } catch (error) {
      // Keep existing map; do not stamp lastFetchTime so we retry soon.
      broadcastCacheState();
      throw error;
    } finally {
      globalIsLoading = false;
      globalFetchPromise = null;
      broadcastCacheState();
    }
  })();

  return globalFetchPromise;
}

export const useAudioCache = () => {
  const [audioCache, setAudioCache] = useState(globalAudioCache);
  const [isLoading, setIsLoading] = useState(globalIsLoading);
  const [lastFetchTime, setLastFetchTime] = useState(globalLastFetchTime);
  const isInitialized = useRef(false);

  const isCacheValid = useCallback(() => isCacheValidNow(), []);

  const fetchUserAudioFiles = useCallback(async (forceRefresh = false) => {
    return prefetchUserAudioFiles({ force: forceRefresh, userId: globalCacheUserId });
  }, []);

  const getAudioForSession = useCallback((sessionId) => {
    if (sessionId == null || sessionId === '') return null;
    const sid = String(sessionId);
    return (
      globalAudioCache.get(sid) ||
      audioCache.get(sid) ||
      globalAudioCache.get(sessionId) ||
      audioCache.get(sessionId) ||
      null
    );
  }, [audioCache]);

  /**
   * Returns a playable URL for a session.
   * Uses a cached local blob: URL when available (instant seek / real duration).
   */
  const getPlayableAudioUrl = useCallback(async (sessionId, options = {}) => {
    try {
      const sid = String(sessionId);
      const audioFile = getAudioForSession(sid);
      if (!audioFile || audioFile.isExpired) {
        return null;
      }

      const cached = globalBlobUrlCache.get(sid);
      if (cached?.blobUrl) {
        touchBlobCache(sid);
        return cached.blobUrl;
      }

      // Reuses the cached signed URL while it is fresh; otherwise fetches a new one (+ records its expiry).
      const signed = await fetchSignedAudioUrl(sid, options);
      if (!signed?.url) return null;
      const signedUrl = signed.url;

      const filename = String(audioFile.filename || audioFile.s3Key || '').toLowerCase();
      const isLiveWebm =
        filename.endsWith('.webm') ||
        filename.includes('live_recording') ||
        String(audioFile.contentType || audioFile.mimeType || '').includes('webm');
      const tooLarge = audioFile.size && audioFile.size > MAX_BLOB_BYTES;

      const ensureWarm = () => {
        if (tooLarge || blobInflight.has(sid) || globalBlobUrlCache.has(sid)) {
          return blobInflight.get(sid) || null;
        }
        const warm = (async () => {
          try {
            const { blobUrl, size } = await fetchAsBlobUrl(signedUrl);
            if (!globalBlobUrlCache.has(sid)) {
              globalBlobUrlCache.set(sid, {
                blobUrl,
                createdAt: Date.now(),
                size,
              });
              evictOldestBlobIfNeeded();
              return blobUrl;
            }
            try {
              URL.revokeObjectURL(blobUrl);
            } catch {
              /* ignore */
            }
            return globalBlobUrlCache.get(sid)?.blobUrl || null;
          } catch {
            return null;
          }
        })();
        blobInflight.set(sid, warm);
        warm.finally(() => {
          if (blobInflight.get(sid) === warm) blobInflight.delete(sid);
        });
        return warm;
      };

      if (isLiveWebm && !tooLarge) {
        const warm = ensureWarm();
        if (warm) {
          try {
            const blobUrl = await Promise.race([
              warm,
              new Promise((resolve) => setTimeout(() => resolve(null), 12000)),
            ]);
            if (blobUrl) return blobUrl;
          } catch {
            /* fall through to signed URL */
          }
        }
      } else {
        ensureWarm();
      }

      return signedUrl;
    } catch (error) {
      return null;
    }
  }, [getAudioForSession]);

  const getCachedBlobUrl = useCallback((sessionId) => {
    const sid = String(sessionId);
    const cached = globalBlobUrlCache.get(sid);
    if (cached?.blobUrl) {
      touchBlobCache(sid);
      return cached.blobUrl;
    }
    return null;
  }, []);

  const awaitCachedBlobUrl = useCallback(async (sessionId) => {
    const sid = String(sessionId);
    const ready = getCachedBlobUrl(sid);
    if (ready) return ready;
    const inflight = blobInflight.get(sid);
    if (inflight) {
      try {
        return (await inflight) || null;
      } catch {
        return null;
      }
    }
    return null;
  }, [getCachedBlobUrl]);

  const cacheLocalAudioBlob = useCallback((sessionId, fileOrBlob) => {
    if (!sessionId || !fileOrBlob) return null;
    const sid = String(sessionId);
    try {
      const existing = globalBlobUrlCache.get(sid);
      if (existing?.blobUrl) {
        try {
          URL.revokeObjectURL(existing.blobUrl);
        } catch {
          /* ignore */
        }
      }
      const blobUrl = URL.createObjectURL(fileOrBlob);
      globalBlobUrlCache.set(sid, {
        blobUrl,
        createdAt: Date.now(),
        size: fileOrBlob.size || 0,
      });
      evictOldestBlobIfNeeded();
      return blobUrl;
    } catch {
      return null;
    }
  }, []);

  const registerSessionAudio = useCallback((sessionId, fileOrMeta = null) => {
    if (!sessionId) return null;
    const sid = String(sessionId);
    const looksLikeFile =
      fileOrMeta &&
      typeof fileOrMeta === 'object' &&
      (typeof fileOrMeta.size === 'number' || typeof fileOrMeta.name === 'string') &&
      (typeof Blob !== 'undefined'
        ? fileOrMeta instanceof Blob || fileOrMeta instanceof File
        : true);
    const file = looksLikeFile ? fileOrMeta : null;
    const meta = !file && fileOrMeta && typeof fileOrMeta === 'object' ? fileOrMeta : null;

    if (file) {
      cacheLocalAudioBlob(sid, file);
    }

    const prev = globalAudioCache.get(sid);
    const next = {
      ...(prev || {}),
      ...(meta || {}),
      sessionId: sid,
      filename: file?.name || meta?.filename || prev?.filename || 'recording.webm',
      size: file?.size ?? meta?.size ?? prev?.size ?? 0,
      contentType:
        file?.type || meta?.contentType || prev?.contentType || 'audio/webm',
      lastModified:
        meta?.lastModified || prev?.lastModified || new Date().toISOString(),
      isExpired: false,
      // Local blob / just-recorded file is playable immediately; don't trap UI in "Uploading…".
      isAccessible: true,
      isOptimistic: true,
      uploadStatus: file || meta?.s3Key ? 'ready' : (meta?.uploadStatus || 'pending_s3'),
    };

    const updated = new Map(globalAudioCache);
    updated.set(sid, next);
    globalAudioCache = updated;
    broadcastCacheState();
    return next;
  }, [cacheLocalAudioBlob]);

  const clearCache = useCallback(() => {
    clearAudioInventory(globalCacheUserId);
  }, []);

  useEffect(() => {
    const listener = ({
      audioCache: nextCache,
      isLoading: nextLoading,
      lastFetchTime: nextFetchTime,
    }) => {
      setAudioCache(nextCache);
      setIsLoading(nextLoading);
      setLastFetchTime(nextFetchTime);
    };
    cacheListeners.add(listener);
    // Sync latest global state into this hook instance (e.g. after login prefetch).
    setAudioCache(globalAudioCache);
    setIsLoading(globalIsLoading);
    setLastFetchTime(globalLastFetchTime);

    if (!isInitialized.current) {
      isInitialized.current = true;
      if (globalAudioCache.size === 0 || !isCacheValidNow()) {
        prefetchUserAudioFiles({ force: globalAudioCache.size === 0 }).catch(() => {});
      }
    }
    return () => {
      cacheListeners.delete(listener);
    };
  }, []);

  const triggerAudioFetch = useCallback(() => {
    return prefetchUserAudioFiles({ force: true, userId: globalCacheUserId });
  }, []);

  return {
    audioCache,
    isLoading,
    fetchUserAudioFiles,
    triggerAudioFetch,
    getAudioForSession,
    getPlayableAudioUrl,
    keepAudioElementUrlFresh,
    ensureAudioElementUrlFresh,
    getCachedBlobUrl,
    awaitCachedBlobUrl,
    cacheLocalAudioBlob,
    registerSessionAudio,
    clearCache,
    isCacheValid,
    lastFetchTime,
  };
};
